import { EMBED_READY_MESSAGE_TYPE, VIVIAN_EVENTS } from "@vivian/shared-contracts";

/** Shape of the raw postMessage payload sent by the embed app. */
interface RelayMessage {
  type: string;
  payload?: unknown;
}

// Allowlist of known vivian:* event names, derived from the shared contract
// (single source of truth). Anything a postMessage sender claims as `type`
// that doesn't map onto this set is ignored — the relay only translates
// wire messages into events we actually define, never arbitrary strings.
const KNOWN_EVENT_NAMES = new Set<string>(Object.values(VIVIAN_EVENTS));

/** Callback invoked with the CustomEvent that should be dispatched on the host element. */
export type RelayDispatch = (event: CustomEvent) => void;

function isRelayMessage(data: unknown): data is RelayMessage {
  return (
    typeof data === "object" &&
    data !== null &&
    "type" in data &&
    typeof (data as { type: unknown }).type === "string"
  );
}

/**
 * Builds a `window` "message" event handler that safely relays messages
 * coming from the embed iframe out to the host page as `vivian:*` CustomEvents.
 *
 * A message is only relayed when BOTH of the following hold — anything else
 * is ignored silently, since `window` receives postMessage traffic from any
 * frame on the page, not just our own iframe:
 *   - `event.origin` matches the expected `embedOrigin` exactly.
 *   - `event.source` matches the expected `iframeWindow` reference exactly.
 *
 * `onEmbedReady` (F7: apiKey handshake) is optional and reuses this SAME
 * validated listener rather than standing up a second `window.addEventListener("message")`
 * — the only difference is that a message whose `type` is
 * `EMBED_READY_MESSAGE_TYPE` is intercepted BEFORE the known-public-event
 * check below and never reaches `dispatch`, so it can never surface as a
 * `vivian:*` CustomEvent on the host element (it isn't part of
 * `VIVIAN_EVENTS`/`KNOWN_EVENT_NAMES` to begin with, but the early return
 * makes that explicit rather than incidental). Not one-shot: every valid
 * `embed-ready` message invokes it again, so a second handshake attempt
 * (e.g. the embed app re-mounting) is handled the same way as the first.
 *
 * Pure function: given the same inputs it always returns an equivalent
 * handler, and the handler's own behavior depends only on the MessageEvent
 * it receives.
 */
export function createMessageRelay(
  embedOrigin: string,
  iframeWindow: MessageEventSource | Window | null,
  dispatch: RelayDispatch,
  onEmbedReady?: () => void,
): (event: MessageEvent) => void {
  return (event: MessageEvent): void => {
    if (event.origin !== embedOrigin) {
      return;
    }
    if (event.source !== iframeWindow) {
      return;
    }
    if (!isRelayMessage(event.data)) {
      return;
    }

    const { type, payload } = event.data;

    if (type === EMBED_READY_MESSAGE_TYPE) {
      onEmbedReady?.();
      return;
    }

    const eventName = `vivian:${type}`;
    if (!KNOWN_EVENT_NAMES.has(eventName)) {
      return;
    }
    dispatch(new CustomEvent(eventName, { detail: payload, bubbles: true, composed: true }));
  };
}
